import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wrench, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { homeFor } from "../lib/areas";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [needPhone, setNeedPhone] = useState(false);
  const [otp, setOtp] = useState("");
  const [needOtp, setNeedOtp] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email.trim(), password, phone, otp.trim());
    setSubmitting(false);
    if (res.needOtp) { setNeedOtp(true); toast.info(res.error); return; }
    if (res.needPhone) { setNeedPhone(true); toast.info(res.error); return; }
    if (res.ok) {
      toast.success(`Hoş geldin, ${res.user.name}`);
      navigate(res.user.role === "admin" ? homeFor(res.user) : "/tech", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left — Form */}
      <div className="flex flex-col justify-between p-8 lg:p-12">
        <div className="flex items-center gap-3">
          <img src="/brand/footer-logo.png" alt="ServisPlus" className="h-14 w-auto object-contain" />
        </div>

        <div className="max-w-md w-full mx-auto py-12">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4">
              <ShieldCheck className="h-3.5 w-3.5" /> Personel Girişi
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
              Hoş geldiniz.
            </h1>
            <p className="text-slate-600 mt-3 leading-relaxed">
              E-posta ve şifrenizle giriş yapın. Sistem yetkinize göre sizi yönlendirir.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {needPhone && (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="login-phone-step">
                <Label htmlFor="phone" className="text-amber-900 font-semibold">Güvenlik doğrulaması</Label>
                <p className="text-xs text-amber-800 mt-1 mb-2">Yeni cihaz algılandı. Hesabınıza kayıtlı cep telefonu numaranızı girin.</p>
                <Input id="phone" inputMode="tel" autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xx xxx xx xx" data-testid="login-phone-input" />
              </div>
            )}
            {needOtp && (
              <div className="p-4 rounded-lg border border-blue-200 bg-blue-50" data-testid="login-otp-step">
                <Label htmlFor="otp" className="text-blue-900 font-semibold">2 adımlı doğrulama</Label>
                <p className="text-xs text-blue-800 mt-1 mb-2">Doğrulayıcı uygulamanızdaki 6 haneli kodu (veya bir yedek kodu) girin.</p>
                <Input id="otp" inputMode="numeric" autoFocus autoComplete="one-time-code" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123456" data-testid="login-otp-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@servis.com"
                required
                data-testid="login-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Şifre</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-900 rounded"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Giriş Yap
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-blue-50/60 border border-blue-100 text-sm">
            <div className="font-semibold text-slate-900">Güvenli Personel Erişimi</div>
            <div className="text-slate-600 mt-1 leading-relaxed">
              Kullanıcı bilgilerinizi sistem yöneticisinden alınız. Şüpheli giriş denemeleri kayıt altına alınır.
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-400">
          © {new Date().getFullYear()} ServisPlus • Tüm hakları saklıdır.
        </div>
      </div>

      {/* Right — Hero */}
      <div className="hidden lg:flex relative bg-slate-950 overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 grain-bg opacity-50" />
        <div className="relative z-10 flex flex-col items-center text-center text-white">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md"><img src="/brand/footer-logo.png" alt="ServisPlus" className="w-full object-contain" /></div>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="font-display text-3xl font-bold">17+</div>
              <div className="text-xs text-white/60 mt-1">Türkiye Markası</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">PDF</div>
              <div className="text-xs text-white/60 mt-1">Servis Fişi</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">%100</div>
              <div className="text-xs text-white/60 mt-1">Saha Hazır</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
